import React, { ReactNode, useCallback } from "react";
import styled from "styled-components";
import { roundToDigits, shorten } from "@backpacker/number";
import {
  FontWeight,
  Radius,
  Spacing,
  ZIndexes,
  content,
  size,
  transparentize,
} from "@glossy";
import { Locale } from "@linkfluence/models/locale";
import { percent } from "@tootools/number";
import Tooltip from "@ui-kit/tooltip";
import { Inner } from "@ui-kit/tooltip/description";
import { Item } from "./model";

export interface IProps {
  item: Item;
  locale: Locale;
  direction: "row" | "column";
  itemMaxWidth: string;
  total?: number;
  displayValue?: "percentage" | "absolute";
  displayVolumes?: boolean;
  opacity?: number;
  magicWandCursor?: boolean;
  setHovered?: (id: string | number) => void;
  tooltip?: (serie: Item) => ReactNode;
  onClick?: (serie: Item["id"]) => void;
}

const Root = styled.div<{
  direction?: IProps["direction"];
  opacity?: IProps["opacity"];
  itemMaxWidth: string;
  clickable: boolean;
  magicWandCursor?: boolean;
}>`
  display: ${(props) => props.direction === "row" && "inline-"}flex;
  column-gap: ${Spacing.ExtraSmall};
  align-items: center;
  line-height: 24px;
  padding: 0 6px;
  border-radius: ${Radius.Small};
  cursor: ${(props) =>
    props.clickable
      ? props.magicWandCursor
        ? "var(--cursor-magic-wand)"
        : "pointer"
      : "default"};
  opacity: ${(props) => props.opacity};
  margin-top: ${(props) => props.direction === "column" && "6px"};
  margin-right: ${(props) => props.direction === "row" && "4px"};
  :hover {
    background-color: ${(props) =>
      transparentize(0.85, content(props, "primary"))};
  }
`;

export const Dot = styled.div.attrs((props) => ({
  // Avoid generating a lot of classes
  style: {
    background: props.color,
  },
}))<{ color: Item["color"] }>`
  flex-shrink: 0;
  border-radius: 100%;
  ${size(10)};
`;

const Label = styled.span`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: ${FontWeight.Normal};
  color: ${content};
`;

const Value = styled.div<{ displayVolumes?: boolean }>`
  margin-left: ${(props) => (props.displayVolumes ? "-6px" : "6px")};
  font-weight: ${FontWeight.Normal};
  color: ${content};
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  column-gap: ${Spacing.ExtraSmall};
`;

const StyledTooltip = styled(Tooltip)`
  z-index: ${ZIndexes.Tooltips};
`;

const LegendItem = ({
  item,
  direction,
  displayValue,
  locale,
  setHovered,
  tooltip,
  total,
  opacity,
  itemMaxWidth,
  onClick,
  displayVolumes,
  magicWandCursor,
}: IProps) => {
  const _onClick = useCallback(() => {
    if (onClick) {
      onClick(item.id);
    }
  }, [item, onClick]);
  return (
    <StyledTooltip
      key={item.id}
      disabled={opacity !== 1}
      tooltip={
        tooltip ? (
          tooltip(item)
        ) : (
          <Inner
            title={
              <Title>
                {!!item.color && <Dot color={item.color} />}
                <span>{item.label}</span>
              </Title>
            }
            content={`${shorten(locale, item.count)}`}
          />
        )
      }
    >
      <Root
        direction={direction}
        onMouseOver={() => !!setHovered && setHovered(item.id)}
        onMouseLeave={() => !!setHovered && setHovered(undefined)}
        style={{ opacity }}
        itemMaxWidth={itemMaxWidth}
        onClick={_onClick}
        clickable={!!onClick}
        magicWandCursor={magicWandCursor}
      >
        {!!item.color && <Dot color={item.color} />}
        <Label>
          {item.label}
          {displayVolumes && `: ${shorten(locale, item.count)}`}
        </Label>
        {!displayVolumes && displayValue ? (
          <Value>
            {displayValue === "percentage"
              ? total
                ? `${shorten(
                    locale,
                    roundToDigits(percent(item.count, total), 4),
                    3,
                    false,
                    1
                  )}%`
                : null
              : shorten(locale, item.count)}
          </Value>
        ) : null}
      </Root>
    </StyledTooltip>
  );
};

export default LegendItem;
